<script setup lang="ts">
import {computed, ref} from 'vue';
import MoodFace from '@/vue/atoms/mood-face.vue';
import FeelingsPicker from '@/vue/molecules/feelings-picker.vue';
import {useMoods} from '@/ts/composables/pure/use-moods';
import type {MoodDTO} from '@/ts/types/dto/journals-dto';
import type {JournalEntryDraft, JournalFeelingOption} from '@/ts/types/component/today-page';

/**
 * Three-step entry mask: mood → note → feelings. Only the mood is required;
 * the note and feelings are both optional, so smiley-only, text-only and both
 * are all savable (matches the backend contract).
 *
 * One step is visible at a time, driven by `step` — the same shape as the
 * design's `data-step` attribute.
 */
defineProps<{
  /**
   * Feelings offered in step 3, keyed by mood so the suggestions relate to what
   * the user picked. A `default` list is used for moods with no family.
   */
  feelingsByMood?: Record<string, JournalFeelingOption[]>;
  defaultFeelings?: JournalFeelingOption[];
  saving?: boolean;
  /**
   * Whether there is an intro behind the mask to go back to. Without one the
   * mask is where the tile rests, so no close is offered at all.
   */
  hasIntro?: boolean;
}>();

const emit = defineEmits<{
  save: [payload: JournalEntryDraft];
  cancel: [];
}>();

const {moods} = useMoods();

const step = ref<1 | 2 | 3>(1);
const mood = ref<MoodDTO | null>(null);
const text = ref('');
const feelings = ref<JournalFeelingOption[]>([]);

/** Which mood carries the group's single tab stop; see `onMoodKeydown`. */
const focusedMood = ref(0);
const moodButtons = ref<HTMLButtonElement[]>([]);

/** Both axes move the selection: the moods are drawn as a grid but read as one list. */
const MOOD_ARROW_STEP: Record<string, number> = {
  ArrowRight: 1,
  ArrowDown: 1,
  ArrowLeft: -1,
  ArrowUp: -1,
};

const canAdvance = computed(() => mood.value !== null);

function selectMood(value: MoodDTO): void {
  mood.value = value;
  focusedMood.value = moods.findIndex((option) => option.key === value);
}

function onMoodKeydown(event: KeyboardEvent, index: number): void {
  const offset = MOOD_ARROW_STEP[event.key] ?? 0;
  if (offset === 0) {
    return;
  }
  event.preventDefault();
  // Wraps, as the pattern expects, so the ends of the list are not dead stops.
  const next = (index + offset + moods.length) % moods.length;
  focusedMood.value = next;
  // Moving the focus selects, which is the single-select radiogroup behaviour:
  // what the user hears as they arrow through is what they would save.
  mood.value = moods[next].key;
  moodButtons.value[next]?.focus();
}

/** The picker draws the search box; the draft it belongs to lives here. */
const search = ref('');

function reset(): void {
  step.value = 1;
  mood.value = null;
  text.value = '';
  feelings.value = [];
  search.value = '';
  focusedMood.value = 0;
}

/**
 * Abandons the entry: the draft is dropped and the tile is asked to close the
 * mask and show its intro again. Only reachable from the empty state.
 */
function cancel(): void {
  reset();
  emit('cancel');
}

function save(): void {
  if (!mood.value) {
    return;
  }
  emit('save', {
    mood: mood.value,
    text: text.value.trim(),
    feelingIds: feelings.value.map((feeling) => feeling.id),
  });
}
</script>

<template>
  <form class="entry-form" :data-step="step" autocomplete="off" @submit.prevent>
    <!-- The design hides its Cancel here, which leaves no way out of the mask
         once it is open. This close button restores that exit, but only for the
         empty state: with entries the mask is where the tile rests, so there is
         nothing to dismiss it back to. -->
    <button
      v-if="hasIntro"
      type="button"
      class="ef-close"
      :aria-label="$t('journal_tile_close')"
      @click="cancel"
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.2"
        stroke-linecap="round"
        aria-hidden="true"
      ><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>
    </button>
    <!-- Step 1 — mood (required) -->
    <div v-show="step === 1" class="ef-step ef-step-1">
      <div class="ef-title">
        {{ $t('journal_tile_mood_question') }}
      </div>
      <div class="ef-group ef-grow">
        <div class="ef-moods" role="radiogroup" :aria-label="$t('journal_tile_mood_question')">
          <button
            v-for="(option, index) in moods"
            :key="option.key"
            ref="moodButtons"
            type="button"
            class="ef-mood"
            :class="{'is-sel': mood === option.key}"
            role="radio"
            :aria-checked="mood === option.key"
            :aria-label="$t(`mood_${option.key}`)"
            :tabindex="index === focusedMood ? 0 : -1"
            @click="selectMood(option.key)"
            @keydown="onMoodKeydown($event, index)"
          >
            <mood-face :mood="option.key" :size="44" tinted class="face"/>
            <span class="lab">{{ $t(`mood_${option.key}`) }}</span>
          </button>
        </div>
      </div>
      <div class="ef-actions">
        <button type="button" class="btn-pill ef-next" :disabled="!canAdvance" @click="step = 2">
          {{ $t('journal_tile_next') }}
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          ><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
        </button>
      </div>
    </div>

    <!-- Step 2 — note (optional) -->
    <div v-show="step === 2" class="ef-step ef-step-2">
      <div class="ef-step-head">
        <button type="button" class="ef-back" :aria-label="$t('journal_tile_back')" @click="step = 1">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          ><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
        </button>
        <div>
          <div class="ef-title">
            {{ $t('journal_tile_note_title') }}
          </div>
          <span class="ef-optional">{{ $t('journal_tile_optional') }}</span>
        </div>
      </div>
      <div class="ef-group ef-grow">
        <div class="ef-label">
          {{ $t('journal_tile_note_label') }}
        </div>
        <div class="ef-text-wrap">
          <textarea
            v-model="text"
            class="ef-text"
            :placeholder="$t('journal_tile_note_placeholder')"
          />
        </div>
      </div>
      <div class="ef-actions">
        <button type="button" class="btn-pill ef-next" @click="step = 3">
          {{ $t('journal_tile_next') }}
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          ><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
        </button>
      </div>
    </div>

    <!-- Step 3 — feelings (optional) -->
    <div v-show="step === 3" class="ef-step ef-step-3">
      <div class="ef-step-head">
        <button type="button" class="ef-back" :aria-label="$t('journal_tile_back')" @click="step = 2">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          ><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
        </button>
        <div>
          <div class="ef-title">
            {{ $t('journal_tile_feelings_title') }}
          </div>
          <span class="ef-optional">{{ $t('journal_tile_optional') }}</span>
        </div>
      </div>
      <feelings-picker
        v-model="feelings"
        v-model:search="search"
        class="ef-group ef-grow ef-feelings-step"
        :feelings-by-mood="feelingsByMood"
        :fallback-feelings="defaultFeelings"
        :mood="mood"
      />
      <div class="ef-actions">
        <button type="button" class="btn-pill ef-save" :disabled="saving" @click="save">
          {{ $t('journal_tile_save') }}
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.4"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          ><polyline points="20 6 9 17 4 12"/></svg>
        </button>
      </div>
    </div>
  </form>
</template>

<style scoped lang="scss">
.entry-form {
  // Surfaces and ink as locals so dark mode swaps the whole card at once. All
  // but the label follow the scheme; the card and its fields take a different
  // step of the same scale in dark mode, which is the dark block at the bottom.
  --ef-surface: var(--Neutral-UI-neutral-ui-0);
  --ef-field: var(--Neutral-UI-neutral-ui-10);
  --ef-ink: var(--Neutral-UI-neutral-ui-100);
  --ef-ink-2: var(--Neutral-UI-neutral-ui-70);
  --ef-ink-3: var(--Neutral-UI-neutral-ui-50);
  // The design's own label grey, which sits between ui-50 and ui-70 and is on
  // neither. Kept as drawn rather than rounded to a step: it is a label on the
  // card's paper, and the nearer step loses contrast against it.
  --ef-label: #58585a;
  // Label colour on the dark pill buttons; not the card surface, which takes a
  // different step in dark mode.
  --ef-on-ink: var(--Neutral-UI-neutral-ui-0);

  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  height: 100%;
  background: var(--ef-surface);
  border-radius: 24px;
  padding: 24px;
  box-shadow: 0 8px 34px rgb(0 0 0 / 7%);
}

.ef-close {
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 2;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 100px;
  background: rgb(32 40 46 / 7%);
  color: var(--ef-ink-2);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 180ms ease, transform 180ms ease, color 180ms ease;

  @include focus-visible;

  &:hover {
    background: rgb(32 40 46 / 14%);
    color: var(--ef-ink);
    // Mirrors the design's modal close, which turns as it is hovered.
    transform: rotate(90deg);
  }
}

.ef-step {
  display: flex;
  flex-direction: column;
  gap: 12px;
  // Grows with the card rather than relying on `height: 100%` alone: the column
  // above is content-sized with a floor, and a percentage against that resolves
  // to auto — which left the note field at its 74px minimum instead of taking
  // the room the design gives it.
  flex: 1 1 auto;
  height: 100%;
  min-height: 0;
}

.ef-grow {
  flex: 1 1 auto;
  min-height: 0;
}

.ef-step-1 .ef-group.ef-grow {
  justify-content: center;
}

.ef-step-1 .ef-title {
  padding-right: 40px;
}

.ef-title {
  font-family: var(--Font-Secondary-Font);
  font-weight: 200;
  font-size: 30px;
  line-height: 27px;
  letter-spacing: -0.75px;
  color: var(--ef-ink);
}

.ef-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ef-label {
  font-weight: 600;
  font-size: 12px;
  line-height: 18px;
  letter-spacing: 0.3px;
  color: var(--ef-ink-2);
}

.ef-step-head {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding-right: 40px;
}

.ef-back {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 100px;
  background: rgb(32 40 46 / 7%);
  color: var(--ef-ink);
  border: 0;
  flex-shrink: 0;
  cursor: pointer;
  font-size: 18px;
  transition: background 150ms ease;

  &:hover {
    background: rgb(32 40 46 / 14%);
  }
}

.ef-optional {
  display: inline-block;
  margin-top: 2px;
  font-weight: 700;
  font-size: 8px;
  line-height: 12px;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: var(--ef-label);
  background: rgb(32 40 46 / 7%);
  border-radius: 100px;
  padding: 3px 6px;
}

.ef-moods {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px 10px;
}

.ef-mood {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  padding: 6px;
  border-radius: 16px;
  background: transparent;
  border: 0;
  cursor: pointer;
  transition: background 160ms ease, opacity 160ms ease;

  @include focus-visible;

  &:hover {
    background: rgb(169 197 195 / 22%);
  }

  .face {
    transition: transform 240ms cubic-bezier(0.22, 1, 0.36, 1);
  }

  .lab {
    font-weight: 600;
    font-size: 12px;
    line-height: 18px;
    letter-spacing: 0.3px;
    color: var(--ef-label);
    white-space: nowrap;
    text-transform: capitalize;
    transition: color 160ms ease;
  }

  &.is-sel {
    background: color-mix(in srgb, var(--Neutral-UI-neutral-ui-0) 88%, var(--Neutral-UI-neutral-ui-100));

    .lab {
      color: var(--ef-ink);
    }
  }
}

.ef-moods:has(.is-sel) .ef-mood:not(.is-sel) {
  opacity: 0.4;
}

.ef-text-wrap {
  position: relative;
  background: var(--ef-field);
  border-radius: 16px;
  padding: 16px 16px 10px;
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
  flex-direction: column;
  gap: 8px;
}

.ef-text {
  border: 0;
  background: transparent;
  resize: none;
  width: 100%;
  flex: 1 1 auto;
  min-height: 74px;
  font-weight: 500;
  // Floored, not the 14 the design draws: below 16px iOS Safari zooms the page on focus and, with
  // pinch zoom allowed, never zooms back out (IPD-3064). This is the note a mobile user types
  // into most, so it is the one that mattered.
  font-size: max(16px, var(--Typography-Body));
  line-height: 1.5;
  color: var(--ef-ink);
  outline: none;

  &::placeholder {
    color: var(--ef-ink-3);
  }
}

.ef-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: auto;
}

.btn-pill {
  height: 48px;
  padding: 0 24px;
  border-radius: 100px;
  background: var(--ef-ink);
  color: var(--ef-on-ink);
  border: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-family: var(--Font-Primary-Font);
  font-weight: 700;
  font-size: 14px;
  line-height: 21px;
  letter-spacing: 0.3px;
  cursor: pointer;
  transition: transform 160ms ease, opacity 160ms ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
}

.ef-feelings-step {
  // 12, from the prototype: the chosen-feelings field, the search and the list
  // are three separate things and 8 ran them together.
  gap: 12px;
}

@media (prefers-reduced-motion: reduce) {
  .ef-mood,
  .ef-mood .face,
  .btn-pill {
    transition: none;
  }
}

/* Dark mode: the mask becomes a dark card with light ink. It sits inside the
   sage panel, so every surface has to be set explicitly rather than inherited. */
@include dark-mode {
  .entry-form {
    // The card and its fields swap places on the scale here: the design draws
    // the card one step up from the darkest and the fields at the darkest, the
    // inverse of the light scheme. These two land on exactly what the block
    // restated by hand (#293139 and #20282E). The ink values no longer need a
    // line at all — following the scheme moves them a shade off the prototype's
    // greys and onto the scheme's own, which is the point.
    --ef-surface: var(--Neutral-UI-neutral-ui-10);
    --ef-field: var(--Neutral-UI-neutral-ui-0);
    --ef-label: var(--Neutral-UI-neutral-ui-50);
  }
}
</style>
